import type { CodeLanguage } from "../model/types.js";
import type {
  SerializedSyntaxHighlightError,
  SyntaxHighlightWorkerRequest,
  SyntaxHighlightWorkerResponse,
} from "./SyntaxHighlightProtocol.js";

interface PendingHighlight {
  readonly resolve: (html: string) => void;
  readonly reject: (reason: unknown) => void;
}

function deserializeError(serialized: SerializedSyntaxHighlightError): Error {
  const error = new Error(serialized.message);
  error.name = serialized.name;
  if (serialized.stack !== null) {
    error.stack = serialized.stack;
  }
  return error;
}

/** Main-thread proxy for the Shiki syntax-highlighting worker. */
export class SyntaxHighlightService {
  private readonly worker: Worker;
  private readonly pendingHighlights = new Map<number, PendingHighlight>();
  private nextRequestId = 1;
  private workerFailure: Error | null = null;

  public constructor() {
    this.worker = new Worker(
      new URL("./SyntaxHighlightWorker.ts", import.meta.url),
      {
        name: "jd-core-syntax-highlight",
        type: "module",
      },
    );
    this.worker.onmessage = (
      event: MessageEvent<SyntaxHighlightWorkerResponse>,
    ): void => {
      this.handleResponse(event.data);
    };
    this.worker.onerror = (event: ErrorEvent): void => {
      this.failWorker(
        new Error(event.message || "The syntax highlighting worker failed"),
      );
    };
    this.worker.onmessageerror = (): void => {
      this.failWorker(
        new Error("The syntax highlighting worker returned an unreadable message"),
      );
    };
  }

  public highlight(source: string, language: CodeLanguage): Promise<string> {
    if (this.workerFailure !== null) {
      return Promise.reject(this.workerFailure);
    }

    const requestId = this.nextRequestId++;
    const request: SyntaxHighlightWorkerRequest = {
      type: "highlight",
      requestId,
      source,
      language,
    };

    return new Promise<string>((resolve, reject) => {
      this.pendingHighlights.set(requestId, { resolve, reject });
      this.worker.postMessage(request);
    });
  }

  private handleResponse(response: SyntaxHighlightWorkerResponse): void {
    const pending = this.pendingHighlights.get(response.requestId);
    if (pending === undefined) {
      return;
    }
    this.pendingHighlights.delete(response.requestId);

    switch (response.type) {
      case "highlighted":
        pending.resolve(response.html);
        return;
      case "failed":
        pending.reject(deserializeError(response.error));
    }
  }

  private failWorker(reason: unknown): void {
    if (this.workerFailure !== null) {
      return;
    }
    this.workerFailure =
      reason instanceof Error ? reason : new Error(String(reason));
    for (const pending of this.pendingHighlights.values()) {
      pending.reject(this.workerFailure);
    }
    this.pendingHighlights.clear();
  }
}
